/**
 * Recent activity, from the one thing in CenoX AI that records any: threads.
 *
 * Your own, and the ones colleagues chose to share — exactly what the server's
 * row-level policy lets the caller read. Nothing is invented to fill the space;
 * an empty list says so.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Users } from 'lucide-react';
import type { AiConversationView } from '@cenox/shared';
import { lastActive } from '../../lib/aiConversations';
import { relativeTime } from '../../lib/format';

export function activityLine(c: AiConversationView): string {
  if (!c.mine) return `${c.authorName ?? 'A colleague'} shared a conversation`;
  return c.messageCount > 0 ? 'You continued a conversation' : 'You started a conversation';
}

export function ActivityList({
  rows,
  limit = 5,
  empty = 'Nothing yet. What you do in CenoX will appear here.',
}: {
  rows: AiConversationView[];
  limit?: number;
  empty?: string;
}): ReactNode {
  if (rows.length === 0) return <p className="py-2 text-sm text-slate-500">{empty}</p>;

  return (
    <ol className="relative space-y-0.5">
      {rows.slice(0, limit).map((c) => {
        const Icon = c.mine ? MessageSquare : Users;
        return (
          <li key={c.id}>
            <Link
              to={`/chat/${c.id}`}
              className="group flex items-start gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-slate-100"
            >
              <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 ring-4 ring-white group-hover:bg-white">
                <Icon className="size-3.5" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-slate-900">
                  {c.title ?? 'New conversation'}
                </span>
                <span className="block truncate text-xs text-slate-500">
                  {activityLine(c)} · {relativeTime(lastActive(c))}
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ol>
  );
}
